import React, { useCallback, useEffect, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import moment from 'moment';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { fetchApiTokens, createApiToken, revokeApiToken } from '../../services/api';
import { API_URL } from '../../services/settings';

const SCOPE_LABELS = {
    '*': 'Full access',
    'timesheet:read': 'Read my timesheet',
    'timesheet:write': 'Write my timesheet',
    'reference:read': 'Read projects and types',
};

const DEFAULT_SCOPES = ['timesheet:read', 'timesheet:write', 'reference:read'];

const EXPIRATION_CHOICES = [
    { id: 30, name: '30 days' },
    { id: 90, name: '90 days' },
    { id: 365, name: '1 year' },
    { id: 0, name: 'Never' },
];

// The API returns naive UTC datetimes.
const formatDate = (value) => (value ? moment.utc(value).local().format('DD/MM/YYYY HH:mm') : '-');

const TokenStatus = ({ token }) => {
    const translate = useTranslate();
    if (token.revoked_at) {
        return <Chip size="small" label={translate('Revoked')} />;
    }
    if (!token.is_valid) {
        return <Chip size="small" color="warning" label={translate('Expired')} />;
    }
    return <Chip size="small" color="success" label={translate('Active')} />;
};

const CreateTokenDialog = ({ open, availableScopes, onClose, onCreated }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const [name, setName] = useState('');
    const [scopes, setScopes] = useState(DEFAULT_SCOPES);
    const [expiration, setExpiration] = useState(90);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setName('');
            setScopes(DEFAULT_SCOPES);
            setExpiration(90);
        }
    }, [open]);

    const fullAccess = scopes.includes('*');

    const toggleScope = (scope) => {
        setScopes((current) => (current.includes(scope) ? current.filter((s) => s !== scope) : [...current, scope]));
    };

    const handleSubmit = async () => {
        setSaving(true);
        try {
            const response = await createApiToken({
                name: name.trim(),
                scopes: fullAccess ? ['*'] : scopes,
                expires_at: expiration ? moment().add(expiration, 'days').toISOString() : null,
            });
            if (response.success) {
                onCreated(response.token);
            } else {
                notify(response.message || translate('Unable to create the token'), { type: 'warning' });
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{translate('New API token')}</DialogTitle>
            <DialogContent>
                <TextField
                    label={translate('Name')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    helperText={translate('What is this token for? E.g. "Timesheet script"')}
                    fullWidth
                    autoFocus
                    margin="normal"
                    inputProps={{ maxLength: 128 }}
                />
                <FormControl component="fieldset" margin="normal">
                    <FormLabel component="legend">{translate('Permissions')}</FormLabel>
                    <FormGroup>
                        {availableScopes.map((scope) => (
                            <FormControlLabel
                                key={scope}
                                control={
                                    <Checkbox
                                        checked={fullAccess || scopes.includes(scope)}
                                        disabled={fullAccess && scope !== '*'}
                                        onChange={() => toggleScope(scope)}
                                    />
                                }
                                label={translate(SCOPE_LABELS[scope] || scope)}
                            />
                        ))}
                    </FormGroup>
                </FormControl>
                {fullAccess && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {translate('This token gives access to the whole API. Keep it secret.')}
                    </Alert>
                )}
                <TextField
                    select
                    label={translate('Expiration')}
                    value={expiration}
                    onChange={(e) => setExpiration(e.target.value)}
                    fullWidth
                    margin="normal"
                >
                    {EXPIRATION_CHOICES.map((choice) => (
                        <MenuItem key={choice.id} value={choice.id}>{translate(choice.name)}</MenuItem>
                    ))}
                </TextField>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{translate('ra.action.cancel')}</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={saving || !name.trim() || scopes.length === 0}>
                    {translate('ra.action.create')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const NewTokenDialog = ({ token, onClose }) => {
    const translate = useTranslate();
    const notify = useNotify();

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(token);
            notify(translate('Token copied'));
        } catch (error) {
            notify(translate('Unable to copy the token'), { type: 'warning' });
        }
    };

    return (
        <Dialog open={Boolean(token)} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{translate('Your new API token')}</DialogTitle>
            <DialogContent>
                <Alert severity="info" sx={{ mb: 2 }}>
                    {translate('Copy it now: you will not be able to see it again.')}
                </Alert>
                <TextField
                    value={token || ''}
                    fullWidth
                    InputProps={{
                        readOnly: true,
                        sx: { fontFamily: 'monospace' },
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={handleCopy} edge="end"><ContentCopyIcon /></IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    {translate('Send it in the Authorization header:')}
                </Typography>
                <Box component="pre" sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1, overflowX: 'auto', fontSize: 12 }}>
                    {`curl -H "Authorization: Bearer ${token || ''}" ${API_URL}/time_entries`}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button variant="contained" onClick={onClose}>{translate('Done')}</Button>
            </DialogActions>
        </Dialog>
    );
};

const ApiTokens = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const [tokens, setTokens] = useState([]);
    const [availableScopes, setAvailableScopes] = useState([]);
    const [creating, setCreating] = useState(false);
    const [newToken, setNewToken] = useState(null);

    const load = useCallback(async () => {
        try {
            const response = await fetchApiTokens();
            setTokens(response.results || []);
            setAvailableScopes(response.scopes || []);
        } catch (error) {
            console.error(error);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const handleCreated = (rawToken) => {
        setCreating(false);
        setNewToken(rawToken);
        load();
    };

    const handleRevoke = async (token) => {
        if (!window.confirm(translate('Revoke the token "%{name}"? Scripts using it will stop working.', { name: token.name }))) {
            return;
        }
        const response = await revokeApiToken(token.id);
        if (response.success) {
            notify(translate('Token revoked'));
            load();
        } else {
            notify(response.message || translate('Unable to revoke the token'), { type: 'warning' });
        }
    };

    return (
        <Box px={2} pb={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2} flexWrap="wrap">
                <Typography variant="body2" color="text.secondary">
                    {translate('Personal tokens let your scripts use the API on your behalf.')}
                </Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
                    {translate('New token')}
                </Button>
            </Box>
            <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{translate('Name')}</TableCell>
                            <TableCell>{translate('Token')}</TableCell>
                            <TableCell>{translate('Permissions')}</TableCell>
                            <TableCell>{translate('Last used')}</TableCell>
                            <TableCell>{translate('Expiration')}</TableCell>
                            <TableCell>{translate('Status')}</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tokens.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">{translate('No API token yet')}</TableCell>
                            </TableRow>
                        )}
                        {tokens.map((token) => (
                            <TableRow key={token.id}>
                                <TableCell>{token.name}</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace' }}>{token.token_prefix}…</TableCell>
                                <TableCell>
                                    <Box display="flex" gap={0.5} flexWrap="wrap">
                                        {(token.scopes || []).map((scope) => (
                                            <Chip key={scope} size="small" variant="outlined" label={translate(SCOPE_LABELS[scope] || scope)} />
                                        ))}
                                    </Box>
                                </TableCell>
                                <TableCell>{formatDate(token.last_used_at)}</TableCell>
                                <TableCell>{token.expires_at ? formatDate(token.expires_at) : translate('Never')}</TableCell>
                                <TableCell><TokenStatus token={token} /></TableCell>
                                <TableCell align="right">
                                    {token.is_valid && (
                                        <Button size="small" color="error" onClick={() => handleRevoke(token)}>
                                            {translate('Revoke')}
                                        </Button>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
            <CreateTokenDialog
                open={creating}
                availableScopes={availableScopes}
                onClose={() => setCreating(false)}
                onCreated={handleCreated}
            />
            <NewTokenDialog token={newToken} onClose={() => setNewToken(null)} />
        </Box>
    );
};

export default ApiTokens;
